import type { Ticket as ApiTicket } from '@/api/endpoints';
import {
  AlertCircle,
  Bug,
  CreditCard,
  HelpCircle,
  Wrench,
} from 'lucide-react';

export type DesignTicketType = 'incident' | 'problem' | 'billing' | 'how_to' | 'service_request';
export type DesignSeverity = 'S1' | 'S2' | 'S3' | 'S4';
export type DesignStatus = 'open' | 'in_progress' | 'pending' | 'resolved' | 'closed';

export interface DesignTicket {
  id: string;
  number: number;
  title: string;
  type: DesignTicketType;
  severity: DesignSeverity;
  status: DesignStatus;
  updatedAt: string;
  comments: number;
}

export const typeLabels: Record<DesignTicketType, string> = {
  incident: 'Не запускается',
  problem: 'Ошибка / баг',
  billing: 'Биллинг',
  how_to: 'Вопрос',
  service_request: 'Запрос услуги',
};

export const statusLabels: Record<DesignStatus, string> = {
  open: 'Открыт',
  in_progress: 'В работе',
  pending: 'Ждём вас',
  resolved: 'Решён',
  closed: 'Закрыт',
};

export const severityLabels: Record<DesignSeverity, string> = {
  S1: 'Критично',
  S2: 'Высокая',
  S3: 'Обычная',
  S4: 'Низкая',
};

export const sla: Record<DesignSeverity, string> = {
  S1: '15 минут',
  S2: '1 час',
  S3: '4 часа',
  S4: '1 день',
};

export interface CategoryDef {
  id: DesignTicketType;
  label: string;
  shortLabel: string;
  description: string;
  icon: typeof AlertCircle;
}

export const categories: CategoryDef[] = [
  { id: 'incident', label: 'Не запускается / падает', shortLabel: 'Не запускается', description: 'Клиент, шелл или игра не стартует, вылетает', icon: AlertCircle },
  { id: 'problem', label: 'Ошибка / баг', shortLabel: 'Ошибка / баг', description: 'Что-то работает не так, как должно', icon: Bug },
  { id: 'billing', label: 'Биллинг и оплаты', shortLabel: 'Биллинг', description: 'Списания, баланс, бонусы, чеки', icon: CreditCard },
  { id: 'how_to', label: 'Как сделать?', shortLabel: 'Вопрос', description: 'Вопрос по настройке или использованию', icon: HelpCircle },
  { id: 'service_request', label: 'Запрос услуги', shortLabel: 'Запрос услуги', description: 'Подключения, доступы, изменения тарифа', icon: Wrench },
];

// ---- adapters ----------------------------------------------------------------

export function mapCategory(cat: string): DesignTicketType {
  switch (cat) {
    case 'bug': return 'problem';
    case 'setup': return 'how_to';
    case 'billing': return 'billing';
    case 'consultation': return 'how_to';
    case 'feature_request': return 'service_request';
    case 'emergency': return 'incident'; // обратно к unmapCategory('incident')
    case 'incident': return 'incident';  // legacy: backend enum больше не отдаёт
    default: return 'how_to';
  }
}

// Обратный маппер — для формы создания тикета. ВАЖНО: значения ДОЛЖНЫ совпадать
// с backend enum TicketCategory (bug|setup|billing|consultation|feature_request|
// emergency). Раньше 'incident' → 'incident' — такого значения в enum нет, и
// createTicketSchema.parse падал с «Validation error» (обращение не создать).
export function unmapCategory(type: DesignTicketType): string {
  switch (type) {
    case 'incident': return 'emergency'; // «Не запускается / падает» → emergency
    case 'problem': return 'bug';
    case 'billing': return 'billing';
    case 'how_to': return 'consultation';
    case 'service_request': return 'feature_request';
  }
}

export function mapPriority(prio: string): DesignSeverity {
  switch (prio) {
    case 'critical': return 'S1'; // backend enum → «Критическая» (было: падало в S3)
    case 'urgent': return 'S1';   // legacy
    case 'high': return 'S2';
    case 'normal': return 'S3';
    case 'low': return 'S4';      // legacy
    default: return 'S3';
  }
}

// ВАЖНО: значения ДОЛЖНЫ совпадать с backend enum TicketPriority
// (normal|high|critical). Раньше 'S1' → 'urgent' и 'S4' → 'low' — таких
// значений в enum нет, из-за чего выбор «Критическая» ронял создание тикета
// с «Validation error».
export function unmapPriority(sev: DesignSeverity): string {
  switch (sev) {
    case 'S1': return 'critical'; // «Критическая»
    case 'S2': return 'high';
    case 'S3': return 'normal';
    case 'S4': return 'normal'; // 'low' в enum отсутствует; в форме S4 не выбирают
  }
}

export function mapStatus(status: string): DesignStatus {
  switch (status) {
    case 'new': return 'open';
    case 'in_progress': return 'in_progress';
    case 'waiting_client': return 'pending';
    case 'waiting_specialist': return 'in_progress';
    case 'resolved': return 'resolved';
    case 'closed': return 'closed';
    default: return 'open';
  }
}

export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  if (diff < 60_000) return 'только что';
  const min = Math.round(diff / 60_000);
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} дн назад`;
  return new Date(iso).toLocaleDateString('ru-RU');
}

export function adaptApiTicket(t: ApiTicket): DesignTicket {
  return {
    id: t.id,
    number: t.number,
    title: t.subject,
    type: mapCategory(t.category as string),
    severity: mapPriority(t.priority as string),
    status: mapStatus(t.status as string),
    updatedAt: formatRelativeTime(t.updatedAt || t.createdAt),
    comments: 0,
  };
}
